/**
 * NewReportModal Component
 *
 * Modal for creating a new report by selecting company and site.
 */

import { toast } from '@/lib/toast';
import { useCallback, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  LoadingState,
  ReportUnitPickerCard,
} from '../../../../components';
import { queryKeys } from '@/lib/query-keys';
import type { CompanyWithSites } from '@/data/repos/companiesRepo';
import type { ReportSummary } from '@/data/repos/reportsRepo';
import { createReportFromTemplate } from '@/data/reports/createReportFromTemplate';
import { applyScorecardHistory } from '@/data/reports/scorecardHistory';
import { SCORECARD_HISTORY_POINTS } from '@/data/scorecardConfig';
import type { Company, ReportTemplate, Site } from '@/data/schemas';
import { formatSiteShorthand } from '@/utils/entityIdentity';
import { useSettings } from '@/features/settings/hooks/useSettings';
import { resolveUnitsPlanningSnapshot } from '@/utils/inspectionPlanning';
import { useDataProvider } from '@/data/provider';
import { trackReportCreated } from '@/lib/metrics';

export type NewReportModalMode = 'company' | 'site';

interface NewReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: NewReportModalMode;
  lockedCompanyId?: string;
  preselectedCompanyId?: string;
  preselectedSiteId?: string;
}

export function NewReportModal({
  isOpen,
  onClose,
  mode = 'company',
  lockedCompanyId,
  preselectedCompanyId,
  preselectedSiteId,
}: NewReportModalProps): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { companies: companiesProvider, templates, reports, catalogs } = useDataProvider();
  const { data: settings } = useSettings();

  const initialCompanyId = mode === 'site' ? lockedCompanyId : preselectedCompanyId;
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(initialCompanyId);
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(preselectedSiteId);

  const [companyTouched, setCompanyTouched] = useState(false);
  const [siteTouched, setSiteTouched] = useState(false);

  const resetSelection = useCallback(() => {
    setSelectedCompanyId(initialCompanyId);
    setSelectedSiteId(preselectedSiteId);
    setCompanyTouched(false);
    setSiteTouched(false);
  }, [initialCompanyId, preselectedSiteId]);

  const handleClose = useCallback(() => {
    resetSelection();
    onClose();
  }, [onClose, resetSelection]);

  const resolvedCompanyId =
    mode === 'site'
      ? lockedCompanyId
      : companyTouched
        ? selectedCompanyId
        : (preselectedCompanyId ?? selectedCompanyId);

  const shouldLoadCompanies = mode === 'company';
  const { data: companies, isLoading: isLoadingCompanies } = useQuery({
    queryKey: queryKeys.companies.lists(),
    queryFn: () => companiesProvider.listCompanies(),
    enabled: shouldLoadCompanies && isOpen,
  });

  const { data: selectedCompany, isLoading: isLoadingCompany } = useQuery({
    queryKey: resolvedCompanyId
      ? queryKeys.companies.detail(resolvedCompanyId)
      : queryKeys.companies.all,
    queryFn: () =>
      resolvedCompanyId ? companiesProvider.getCompany(resolvedCompanyId) : Promise.resolve(null),
    enabled: Boolean(resolvedCompanyId) && isOpen,
  });

  const selectedCompanySites = useMemo(
    () => selectedCompany?.sites ?? [],
    [selectedCompany?.sites]
  );
  const hasNoSites = Boolean(resolvedCompanyId) && selectedCompanySites.length === 0;

  const { data: defaultTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: queryKeys.templates.default(),
    queryFn: () => templates.getDefault(),
    enabled: isOpen,
  });

  const { data: companyReports } = useQuery({
    queryKey: resolvedCompanyId
      ? queryKeys.reports.byCompany(resolvedCompanyId)
      : queryKeys.reports.all,
    queryFn: () => (resolvedCompanyId ? reports.list(resolvedCompanyId) : Promise.resolve([])),
    enabled: Boolean(resolvedCompanyId) && isOpen,
  });

  const draftBySiteId = useMemo(() => {
    const entries = companyReports ?? [];
    const map = new Map<string, ReportSummary>();
    entries.forEach((report) => {
      if (report.status !== 'draft') return;
      const existing = map.get(report.siteId);
      if (!existing) {
        map.set(report.siteId, report);
        return;
      }
      const existingTime = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
      const nextTime = report.updatedAt ? new Date(report.updatedAt).getTime() : 0;
      if (nextTime >= existingTime) {
        map.set(report.siteId, report);
      }
    });
    return map;
  }, [companyReports]);

  const autoSiteId = useMemo(() => {
    if (preselectedSiteId && selectedCompanySites.some((site) => site.id === preselectedSiteId)) {
      return preselectedSiteId;
    }

    if (selectedCompanySites.length === 1) {
      return selectedCompanySites[0]?.id;
    }

    return undefined;
  }, [preselectedSiteId, selectedCompanySites]);

  const resolvedSiteId = siteTouched ? selectedSiteId : autoSiteId;

  const selectedSiteDraft = useMemo(
    () => (resolvedSiteId ? draftBySiteId.get(resolvedSiteId) : undefined),
    [draftBySiteId, resolvedSiteId]
  );

  const createReportMutation = useMutation({
    mutationFn: async ({
      company,
      site,
      template,
    }: {
      company: Company;
      site: Site;
      template: ReportTemplate;
    }) => {
      const catalogData = await catalogs.getAllCatalogs();
      const historyReports = await reports.getRecentFinalizedBySite(
        site.id,
        SCORECARD_HISTORY_POINTS
      );
      const previousReport = historyReports[0] ?? null;
      const report = createReportFromTemplate({
        template,
        company,
        site,
        catalogs: catalogData,
        variables: settings?.nostroRecapito
          ? { nostroRecapito: settings.nostroRecapito }
          : undefined,
        previousReport: previousReport ?? undefined,
      });
      applyScorecardHistory(report, historyReports);
      await reports.upsert(report);
      return report;
    },
    onSuccess: (report) => {
      trackReportCreated();
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      toast.success('Report creato con successo');
      navigate({
        to: '/reports/$reportId',
        params: { reportId: report.id },
        search: { mode: 'edit' },
      });
    },
    onError: (error) => {
      console.error('Failed to create report:', error);
      toast.error('Errore nella creazione del report');
    },
  });

  const handleCreate = async () => {
    if (!resolvedCompanyId || !resolvedSiteId) return;

    if (selectedSiteDraft) {
      toast.info('Esiste già una bozza per questo stabilimento');
      handleClose();
      navigate({
        to: '/reports/$reportId',
        params: { reportId: selectedSiteDraft.id },
        search: { mode: 'edit' },
      });
      return;
    }

    const company =
      (selectedCompany as CompanyWithSites | undefined) ??
      (companies?.find((c: Company) => c.id === resolvedCompanyId) as CompanyWithSites | undefined);
    const site = company?.sites.find((s: Site) => s.id === resolvedSiteId);

    if (!company || !site) {
      toast.error('Seleziona azienda e stabilimento');
      return;
    }

    if (!defaultTemplate) {
      toast.error('Template predefinito non disponibile');
      return;
    }

    const existingDraft = await reports.getDraftBySite(site.id);
    if (existingDraft) {
      toast.info('Esiste già una bozza per questo stabilimento');
      handleClose();
      navigate({
        to: '/reports/$reportId',
        params: { reportId: existingDraft.id },
        search: { mode: 'edit' },
      });
      return;
    }

    createReportMutation.mutate({ company, site, template: defaultTemplate });
  };

  const isLoading =
    isLoadingTemplate ||
    (shouldLoadCompanies && isLoadingCompanies) ||
    (resolvedCompanyId ? isLoadingCompany : false);
  const isCreating = createReportMutation.isPending;
  const canCreate =
    Boolean(resolvedCompanyId && resolvedSiteId) &&
    !isCreating &&
    (selectedSiteDraft ? true : Boolean(defaultTemplate));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nuovo Report</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <DialogBody>
            <LoadingState label="Caricamento dati..." />
          </DialogBody>
        ) : (
          <DialogBody className="space-y-5">
            {/* Company Selection */}
            {mode === 'site' ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-text-primary">Azienda</p>
                <div className="w-full rounded-md border border-[var(--input-border-default)] bg-surface px-3 py-2 text-sm text-text-primary">
                  {selectedCompany?.name ?? '—'}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label
                  htmlFor="company-select"
                  className="block text-sm font-medium text-text-primary"
                >
                  Azienda *
                </label>
                <select
                  id="company-select"
                  value={resolvedCompanyId || ''}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value || undefined);
                    setCompanyTouched(true);
                    setSelectedSiteId(undefined);
                    setSiteTouched(false);
                  }}
                  className="w-full rounded-md border border-[var(--input-border-default)] bg-surface text-text-primary px-3 py-2 text-sm transition-all duration-150 hover:border-[var(--input-border-hover)] focus:outline-none focus:border-[var(--input-border-focus)] focus:ring-2 focus:ring-brand-blue-500/30"
                >
                  <option value="">Seleziona un&apos;azienda...</option>
                  {companies?.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Site Selection */}
            {resolvedCompanyId && (
              <div className="space-y-2">
                <label
                  htmlFor="site-select"
                  className="block text-sm font-medium text-text-primary"
                >
                  Stabilimento *
                </label>
                {hasNoSites ? (
                  <div className="rounded-md border border-[var(--border-subtle)] bg-surface px-3 py-2 text-sm text-text-secondary">
                    Nessuno stabilimento disponibile per questa azienda.
                  </div>
                ) : (
                  <div id="site-select" className="grid gap-3 sm:grid-cols-2">
                    {selectedCompanySites.map((site) => {
                      const shorthand = selectedCompany
                        ? formatSiteShorthand(selectedCompany, site)
                        : '';
                      const planningSnapshot = resolveUnitsPlanningSnapshot(
                        site.refrigerationUnits ?? []
                      );
                      const draft = draftBySiteId.get(site.id);
                      return (
                        <ReportUnitPickerCard
                          key={site.id}
                          title={site.name}
                          helper={shorthand ? `Sigla ${shorthand}` : undefined}
                          planningLabel={planningSnapshot.label}
                          isDraft={Boolean(draft)}
                          selected={resolvedSiteId === site.id}
                          onSelect={() => {
                            if (draft) {
                              toast.info('Esiste già una bozza per questo stabilimento');
                              handleClose();
                              navigate({
                                to: '/reports/$reportId',
                                params: { reportId: draft.id },
                                search: { mode: 'edit' },
                              });
                              return;
                            }
                            setSelectedSiteId(site.id);
                            setSiteTouched(true);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </DialogBody>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={isCreating}>
              Annulla
            </Button>
          </DialogClose>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!canCreate || isCreating || hasNoSites}
          >
            {isCreating ? 'Creazione...' : selectedSiteDraft ? 'Apri bozza' : 'Crea Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
